import React, { useEffect, useMemo, useRef } from 'react';
import type { PdfPage } from '../../utils/pdf/pdfjs';
import { planIsCovered, planDraw, RenderPlan, viewportParamsFor } from './pdfRenderMath';

/** Delay after the last zoom/pan change before the visible region is re-rendered. */
export const RERENDER_DEBOUNCE_MS = 100;

interface PdfPageCanvasProps {
    page: PdfPage;
    /** The drawn box in CSS px (unzoomed); its aspect is the drawing node's width:height. */
    boxWidth: number;
    boxHeight: number;
    /** Current CSS transform of the box (translate(pan) scale(zoom), origin 0 0). */
    zoom: number;
    pan: { x: number; y: number };
    /** The clipping container in CSS px. */
    containerWidth: number;
    containerHeight: number;
    /** Called once, after the first vector render is on screen (the raster placeholder can then be hidden). */
    onFirstRender?: () => void;
}

interface RenderTaskLike {
    promise: Promise<void>;
    cancel: () => void;
}

/**
 * The vector layer of a drawing (plan §5.1). Lives inside the transformed box of
 * BlueprintWithMarkers, positioned in box CSS px, so the box's CSS transform pans
 * and scales the last render for free while the next one is prepared; nothing
 * ever blanks. Whole-page renders are kept per zoom bucket; past the pixel
 * budget only the visible region (plus overscan) is drawn, ≈100 ms after the
 * gesture ends. Every render starts from a white fill (pages without a
 * background must not show the raster through).
 */
const PdfPageCanvas: React.FC<PdfPageCanvasProps> = ({
    page,
    boxWidth,
    boxHeight,
    zoom,
    pan,
    containerWidth,
    containerHeight,
    onFirstRender,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderedRef = useRef<RenderPlan | null>(null);
    const taskRef = useRef<RenderTaskLike | null>(null);
    const timerRef = useRef<number | null>(null);
    const generationRef = useRef(0);
    const firstRenderReportedRef = useRef(false);
    const onFirstRenderRef = useRef(onFirstRender);
    useEffect(() => { onFirstRenderRef.current = onFirstRender; }, [onFirstRender]);

    const dpr = typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;

    const plan = useMemo(() => planDraw({
        boxWidth,
        boxHeight,
        zoom,
        pan,
        containerWidth,
        containerHeight,
        dpr,
    }), [boxWidth, boxHeight, zoom, pan, containerWidth, containerHeight, dpr]);

    // A new page (another drawing) invalidates whatever is on the canvas.
    useEffect(() => {
        renderedRef.current = null;
        firstRenderReportedRef.current = false;
    }, [page]);

    // Stable key so a pan that stays inside the rendered region does not restart the timer.
    const planKey = plan
        ? `${plan.mode}|${plan.left.toFixed(2)}|${plan.top.toFixed(2)}|${plan.width.toFixed(2)}|${plan.height.toFixed(2)}|${plan.canvasWidth}|${plan.canvasHeight}|${plan.pixelsPerBoxPx}`
        : '';

    useEffect(() => {
        if (!plan) return;
        if (planIsCovered(renderedRef.current, plan)) return;

        const render = async () => {
            const generation = ++generationRef.current;
            taskRef.current?.cancel();
            taskRef.current = null;

            const offscreen = document.createElement('canvas');
            offscreen.width = plan.canvasWidth;
            offscreen.height = plan.canvasHeight;
            const ctx = offscreen.getContext('2d');
            if (!ctx) return;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, offscreen.width, offscreen.height);

            const pageWidthPt = page.getViewport({ scale: 1 }).width;
            const { scale, offsetX, offsetY } = viewportParamsFor(plan, boxWidth, pageWidthPt);
            const viewport = page.getViewport({ scale, offsetX, offsetY });

            let task: RenderTaskLike;
            try {
                task = page.render({ canvasContext: ctx, viewport, background: '#ffffff' });
            } catch (err) {
                console.warn('[PdfPageCanvas] Kunde inte starta renderingen', err);
                return;
            }
            taskRef.current = task;
            try {
                await task.promise;
            } catch {
                // Cancelled by a newer plan or unmount; nothing to commit.
                return;
            }
            if (generation !== generationRef.current) return;
            taskRef.current = null;

            const visible = canvasRef.current;
            if (!visible) return;
            visible.width = plan.canvasWidth;
            visible.height = plan.canvasHeight;
            visible.style.left = `${plan.left}px`;
            visible.style.top = `${plan.top}px`;
            visible.style.width = `${plan.width}px`;
            visible.style.height = `${plan.height}px`;
            visible.style.visibility = 'visible';
            const visibleCtx = visible.getContext('2d');
            if (!visibleCtx) return;
            visibleCtx.drawImage(offscreen, 0, 0);
            renderedRef.current = plan;

            if (!firstRenderReportedRef.current) {
                firstRenderReportedRef.current = true;
                onFirstRenderRef.current?.();
            }
        };

        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        const delay = renderedRef.current ? RERENDER_DEBOUNCE_MS : 0;
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            void render();
        }, delay);

        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        // planKey stands in for plan; boxWidth is read inside render and changes planKey too.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planKey, page]);

    useEffect(() => () => {
        generationRef.current++;
        taskRef.current?.cancel();
        taskRef.current = null;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="blueprint-vector-layer"
            aria-hidden
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 0,
                height: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
            }}
        />
    );
};

export default PdfPageCanvas;
